import { useCallback, useEffect, useState } from "react";
import {
  Plus, FileText, Search, Clock, AlertTriangle, Loader2, Archive, Copy, Download, Bell,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { STATUSES, STATUS_STYLE, fmtDate } from "./util";

const STAT_DEFS = [
  ["total", "Total"], ["drafts", "Drafts"], ["awaiting_signature", "Awaiting signature"],
  ["active", "Active"], ["expiring_soon", "Expiring soon"], ["completed", "Completed"], ["archived", "Archived"],
];

export default function ContractsHome({ onNew, onOpen }) {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "", sort: "recent" });
  const [loading, setLoading] = useState(true);

  const loadDash = useCallback(() => api.get("/contracts/dashboard").then(({ data }) => setDash(data)).catch(() => {}), []);
  const loadList = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.q) p.set("q", filters.q);
    if (filters.status) p.set("status", filters.status);
    p.set("sort", filters.sort);
    api.get(`/contracts?${p}`).then(({ data }) => { setRows(data); setLoading(false); }).catch(() => setLoading(false));
  }, [filters]);

  useEffect(() => { loadDash(); }, [loadDash]);
  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);

  const archive = async (id) => { try { await api.delete(`/contracts/${id}`); toast.success("Archived"); loadList(); loadDash(); } catch { toast.error("Failed"); } };
  const duplicate = async (id) => { try { await api.post(`/contracts/${id}/duplicate`); toast.success("Duplicated"); loadList(); loadDash(); } catch { toast.error("Failed"); } };

  return (
    <div className="fv-fade-up" data-testid="contracts-home">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Manage</p>
          <h1 className="font-heading text-3xl text-white">Contracts &amp; Agreements</h1>
          <p className="text-sm text-[#71717A] mt-1">Create, edit, sign, and manage your business contracts end-to-end.</p>
        </div>
        <Button onClick={onNew} data-testid="new-contract-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full hover:opacity-90"><Plus className="w-4 h-4 mr-1.5" /> New contract</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="contract-stats">
        {STAT_DEFS.map(([k, label]) => (
          <div key={k} className="rounded-xl border border-white/8 bg-[#121214] p-3.5" data-testid={`stat-${k}`}>
            <div className={`text-2xl font-heading ${k === "expiring_soon" && dash?.stats?.[k] > 0 ? "text-amber-300" : "text-white"}`} style={{ fontVariantNumeric: "tabular-nums" }}>{dash?.stats?.[k] ?? 0}</div>
            <div className="text-[11px] text-[#71717A] mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Reminders */}
      {dash?.reminders?.length > 0 && (
        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/5 p-4 mb-6" data-testid="reminders">
          <div className="flex items-center gap-2 mb-2 text-amber-300 text-sm"><Bell className="w-4 h-4" /> Upcoming reminders</div>
          <div className="space-y-1.5">
            {dash.reminders.slice(0, 5).map((r, i) => (
              <button key={i} onClick={() => onOpen(r.contract_id)} data-testid={`reminder-${i}`} className="w-full flex items-center gap-2 text-left text-xs text-[#D4D4D8] hover:text-white">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="truncate flex-1">{r.title}</span>
                <span className="text-[#A1A1AA]">{r.kind} · {r.days < 0 ? `${-r.days}d ago` : `in ${r.days}d`}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="contracts-search" placeholder="Search by name, party, keyword…"
            className="w-full bg-[#121214] border border-white/10 rounded-full pl-9 pr-4 h-10 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/50" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40 h-10 bg-[#121214] border-white/10 text-white text-xs" data-testid="contracts-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#1C1C1F] border-white/10 text-white"><SelectItem value="all" className="focus:bg-white/10">Any status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-white/10">{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.sort} onValueChange={(v) => setFilters((f) => ({ ...f, sort: v }))}>
          <SelectTrigger className="w-28 h-10 bg-[#121214] border-white/10 text-white text-xs" data-testid="contracts-filter-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#1C1C1F] border-white/10 text-white"><SelectItem value="recent" className="focus:bg-white/10">Recent</SelectItem><SelectItem value="title" className="focus:bg-white/10">Title</SelectItem><SelectItem value="status" className="focus:bg-white/10">Status</SelectItem></SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-20 rounded-2xl border border-dashed border-white/10 bg-[#0f0f11]" data-testid="contracts-empty">
          <FileText className="w-10 h-10 text-[#52525B] mx-auto mb-3" />
          <p className="text-[#71717A] mb-4">No contracts yet. Create your first agreement to get started.</p>
          <Button onClick={onNew} data-testid="empty-new-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Plus className="w-4 h-4 mr-1.5" /> New contract</Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="contracts-list">
          {rows.map((r) => (
            <div key={r.contract_id} data-testid={`contract-row-${r.contract_id}`} className="group flex items-center gap-3 px-3 py-3 rounded-xl border border-white/8 bg-[#121214] hover:border-white/15 transition-colors">
              <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] shrink-0"><FileText className="w-4 h-4" /></div>
              <button onClick={() => onOpen(r.contract_id)} data-testid={`open-contract-${r.contract_id}`} className="min-w-0 flex-1 text-left">
                <div className="text-sm text-white font-medium line-clamp-2 sm:truncate hover:text-[#D4AF37]">{r.title}</div>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-[#71717A]">
                  <span>{r.type_name}</span>{r.counterparty && <><span>·</span><span>{r.counterparty}</span></>}
                  <span>·</span><span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(r.updated_at)}</span>
                  {r.tags?.slice(0, 2).map((t) => <span key={t} className="text-[#D4AF37]/80">#{t}</span>)}
                </div>
              </button>
              <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${STATUS_STYLE[r.status] || "bg-white/8 text-[#A1A1AA]"}`}>{r.status}</span>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => window.open(`${API}/contracts/${r.contract_id}/pdf`, "_blank")} data-testid={`pdf-contract-${r.contract_id}`} title="PDF" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-white hover:bg-white/5 flex items-center justify-center"><Download className="w-4 h-4" /></button>
                <button onClick={() => duplicate(r.contract_id)} data-testid={`dup-contract-${r.contract_id}`} title="Duplicate" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-white hover:bg-white/5 flex items-center justify-center"><Copy className="w-4 h-4" /></button>
                {r.status !== "Archived" && <button onClick={() => archive(r.contract_id)} data-testid={`archive-contract-${r.contract_id}`} title="Archive" className="w-8 h-8 rounded-lg text-[#71717A] hover:text-red-400 hover:bg-white/5 flex items-center justify-center"><Archive className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
